/**
 * Enhanced Button Component
 * Custom variants with gradient effects and ghost styles
 */

import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, styled } from '@mui/material';
import { colors } from '../../theme/colors';

type ButtonVariant = 'gradient' | 'ghost' | 'contained' | 'outlined' | 'text';

interface CustomButtonProps extends Omit<MuiButtonProps, 'variant'> {
  variant?: ButtonVariant;
  loading?: boolean;
}

const GradientButton = styled(MuiButton)(() => ({
  background: colors.primary.gradient,
  color: colors.text.inverse,
  padding: '10px 24px',
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(8, 131, 149, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    background: colors.neutral[200],
    color: colors.text.disabled,
  },
}));

const GhostButton = styled(MuiButton)(() => ({
  background: 'transparent',
  color: colors.primary.main,
  border: `1px solid ${colors.neutral[300]}`,
  padding: '10px 24px',
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: 'rgba(8, 131, 149, 0.05)',
    borderColor: colors.primary.main,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    borderColor: colors.neutral[200],
    color: colors.text.disabled,
  },
}));

export const Button: React.FC<CustomButtonProps> = ({ variant = 'contained', loading, children, disabled, ...props }) => {
  if (variant === 'gradient') {
    return (
      <GradientButton disabled={disabled || loading} {...props}>
        {loading ? 'Загрузка...' : children}
      </GradientButton>
    );
  }

  if (variant === 'ghost') {
    return (
      <GhostButton disabled={disabled || loading} {...props}>
        {loading ? 'Загрузка...' : children}
      </GhostButton>
    );
  }

  return (
    <MuiButton variant={variant} disabled={disabled || loading} {...props}>
      {loading ? 'Загрузка...' : children}
    </MuiButton>
  );
};
